import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { associadoService, type ListFilters } from '@/services/associado.service'
import type { Associado } from '@/types/database'

export function useAssociados(filters: ListFilters = {}) {
    return useQuery({
        queryKey: ['associados', filters],
        queryFn: () => associadoService.list(filters),
    })
}

export function useAssociadosStats() {
    return useQuery({
        queryKey: ['associados-stats'],
        queryFn: () => associadoService.getStatusCounts(),
        staleTime: 30_000, // revalida a cada 30s
    })
}

export function useAssociado(id: string | null) {
    return useQuery({
        queryKey: ['associado', id],
        queryFn: () => associadoService.getById(id!),
        enabled: !!id,
    })
}

export function useAssociadosPorCpf(cpf: string | null) {
    return useQuery({
        queryKey: ['associados-cpf', cpf],
        queryFn: () => associadoService.listByCpf(cpf!),
        enabled: !!cpf,
    })
}

export function useCreateAssociado() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (input: Partial<Associado>) => associadoService.create(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['associados'] }),
    })
}

export function useUpdateAssociado() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, ...input }: Partial<Associado> & { id: string }) => associadoService.update(id, input),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['associados'] })
            qc.invalidateQueries({ queryKey: ['associado'] })
        },
    })
}

export function useDeleteAssociado() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => associadoService.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['associados'] }),
    })
}
